<template>
  <v-card dark class="mx-auto py-3">
    <v-row v-if="loading">
      <v-col cols="12">
        <v-progress-linear class="my-5 px-5" indeterminate color="primary"></v-progress-linear>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" v-if="holidayEvents.length === 0">
        <v-alert class="mx-5" type="info">Keine Daten vorhanden</v-alert>
      </v-col>
      <v-col cols="12" v-else>
        <v-row>
          <v-col cols="12" class="d-flex justify-center my-2 flex-wrap" style="gap: 8px">
            <v-chip color="green" pill>Ferien</v-chip>
            <v-chip color="orange" pill>Offene Ferienanträge</v-chip>
            <v-chip color="red" dark pill>Ausfall (Krank/Anderes)</v-chip>
            <v-chip color="teal" dark pill>Schule</v-chip>
            <v-chip color="brown" dark pill>Militär</v-chip>
            <v-chip color="#9C27B0" dark pill>Freiwunsch</v-chip>
            <v-chip color="#2196F3" dark pill>Schichtwunsch</v-chip>
            <v-chip color="#E1BEE7" light pill>Offener Freiwunsch</v-chip>
            <v-chip color="#BBDEFB" light pill>Offener Schichtwunsch</v-chip>
          </v-col>
        </v-row>
        <v-sheet height="800">
          <v-calendar
            locale="de-CH"
            ref="calendar"
            dark
            v-model="selectedMonthCalendar"
            :now="$moment().format('YYYY-MM-DD')"
            :weekdays="weekdays"
            :type="'month'"
            :events="holidayEvents"
            :event-color="(event) => event.color"
            :event-text-color="'white'"
            event-more-text="{0} weitere anzeigen"
            @click:more="showDay"
            @click:event="showEvent">
            <template #event="{ event }">
              <v-tooltip bottom open-delay="200" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="d-flex justify-center text-center px-1"
                    :style="{ color: event.textColor || 'white', fontSize: '11px', lineHeight: '1.2' }">
                    <div class="text-truncate">
                      {{ event.employeeName }}
                      <span v-if="event.absenceReason === 'other' && event.comment" class="font-italic"> ({{ event.comment }}) </span>
                      <span v-else-if="['sick', 'other'].includes(event.absenceReason)"> ({{ typeItems[event.absenceReason] }}) </span>
                      <span v-if="event.shiftDetails" class="ml-1">{{ event.shiftDetails }}</span>
                    </div>
                  </div>
                </template>
                <div class="pa-1">
                  <div class="font-weight-bold">{{ event.employeeName }}</div>
                  <div>
                    {{ event.name }} <span v-if="event.shiftDetails">{{ event.shiftDetails }}</span>
                  </div>
                  <div v-if="event.comment" class="grey--text text--lighten-2 mt-1">
                    <v-icon x-small color="grey" class="mr-1">mdi-comment-text-outline</v-icon>
                    "{{ event.comment }}"
                  </div>
                </div>
              </v-tooltip>
            </template>
          </v-calendar>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="dayDialog" max-width="560" scrollable>
      <v-card color="#252525" dark class="rounded-lg">
        <v-card-title class="d-block pb-2">
          <div class="text-h6">Abwesenheiten & Wünsche</div>
          <div v-if="selectedDay" class="text-subtitle-1">
            {{ $moment(selectedDay).locale("de").format("dddd, DD.MM.YYYY") }}
          </div>
        </v-card-title>
        <v-card-text class="px-0 pb-0" style="max-height: 65vh">
          <div class="px-6 pb-2 grey--text text--lighten-1">
            {{ selectedDayEvents.length }} {{ selectedDayEvents.length === 1 ? "Eintrag" : "Einträge" }} · Für Details anklicken
          </div>
          <v-list color="transparent" class="py-0">
            <v-list-item v-for="(event, index) in selectedDayEvents" :key="index" @click="showEvent({ nativeEvent: $event, event })">
              <v-list-item-icon class="mr-4 align-self-center">
                <v-icon :color="event.color" small>mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-wrap font-weight-bold">{{ event.employeeName }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap mt-1">
                  {{ event.name }} {{ event.shiftDetails || "" }}<span v-if="event.isPending"> · Offen</span>
                </v-list-item-subtitle>
                <div class="text-caption grey--text text--lighten-1 mt-1">{{ formatEventDates(event) }}</div>
              </v-list-item-content>
              <v-list-item-action class="ml-2">
                <v-icon small>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="justify-end">
          <v-btn text @click="dayDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EVENT DIALOG FOR DETAILS -->
    <v-dialog v-model="eventDialog" max-width="400">
      <v-card color="#252525" dark class="rounded-lg" v-if="selectedEvent">
        <v-card-title class="pb-1" :style="{ color: selectedEvent.color }">
          {{ selectedEvent.employeeName }}
        </v-card-title>
        <v-card-text>
          <div class="text-subtitle-1 white--text font-weight-bold mb-2">
            {{ selectedEvent.name }}
            {{ selectedEvent.shiftDetails ? selectedEvent.shiftDetails : "" }}
            <span v-if="selectedEvent.isPending"> · Offen</span>
          </div>
          <div class="mb-2" v-if="selectedEvent.comment">
            <v-icon x-small color="grey">mdi-comment-text-outline</v-icon>
            <span class="grey--text font-italic ml-2">"{{ selectedEvent.comment }}"</span>
          </div>
          <div class="mb-1 white--text">
            <v-icon x-small class="mr-1">mdi-calendar</v-icon>
            {{ formatEventDates(selectedEvent) }}
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="grey" @click="eventDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"
export default {
  name: "HolidayEntriesView",
  props: {
    datePickerRange: {},
    selectedStudioSingle: {
      required: true,
    },
    studioManager: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      selectedMonth: this.$moment().startOf("month").format("YYYY-MM"),
      datePickerDialog: false,
      holidayEvents: [],
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      typeItems: {
        sick: "Krank",
        school: "Schule",
        military: "Militär",
        pregnancy: "Schwangerschaft",
        other: "Andere",
      },
      eventDialog: false,
      selectedEvent: null,
      dayDialog: false,
      selectedDay: null,
    }
  },
  created() {
    this.fetchEntries()
  },
  methods: {
    async fetchEntries() {
      this.loading = true
      this.dayDialog = false
      this.selectedDay = null
      this.eventDialog = false
      this.selectedEvent = null
      try {
        let data = await api.fetchHolidayEvents({
          datePickerRange: this.datePickerRange,
          selectedStudioSingle: this.selectedStudioSingle,
          studioManager: this.studioManager,
        })

        // Override colors for special absences to differentiate from Sick (red)
        data.forEach((event) => {
          event.name = event.name || event.details || event.type || "Abwesenheit"
          // The holiday overview API currently conveys pending requests through their color.
          event.isPending = event.type === "Ferien" && event.color === "orange"
          if (event.absenceReason === "school") {
            event.color = "teal"
            event.name = "Schule"
          } else if (event.absenceReason === "military") {
            event.color = "brown"
            event.name = "Militär"
          } else if (event.absenceReason === "pregnancy") {
            event.color = "pink"
            event.name = "Schwangerschaft"
          } else if (event.absenceReason === "other") {
            event.name = "Andere"
          } else if (event.absenceReason === "sick") {
            event.name = "Krank"
          }
        })

        this.holidayEvents = data

        if (this.selectedStudioSingle && api.fetchManagementRequests) {
          let startDate = this.datePickerRange[0]
          let endDate = this.datePickerRange[1]
          let openReqs = await api.fetchManagementRequests(this.selectedStudioSingle, "open", startDate, endDate)
          let acceptedReqs = await api.fetchManagementRequests(this.selectedStudioSingle, "accepted", startDate, endDate)

          let reqs = [...openReqs, ...acceptedReqs]
          reqs.forEach((req) => {
            if (req.type === "freewish" || req.type === "shiftwish") {
              let color = req.type === "freewish" ? "#9C27B0" : "#2196F3"
              let textColor = "white"
              if (req.status === "open") {
                color = req.type === "freewish" ? "#E1BEE7" : "#BBDEFB"
                textColor = "black"
              }

              let shiftStr = ""
              if (req.type === "shiftwish" && req.shiftType) {
                const types = { morning: "Frühschicht", intermediate: "Zwischenschicht", late: "Spätschicht" }
                shiftStr = `(${types[req.shiftType] || req.shiftType})`
              } else if (req.type === "freewish" && req.period) {
                const periods = { full: "Ganzer Tag", morning: "Vormittag", afternoon: "Nachmittag" }
                shiftStr = `(${periods[req.period] || req.period})`
              }

              this.holidayEvents.push({
                name: req.type === "freewish" ? "Freiwunsch" : "Schichtwunsch",
                start: this.$moment(req.day).format("YYYY-MM-DD"),
                end: this.$moment(req.day).format("YYYY-MM-DD"),
                color: color,
                textColor: textColor,
                employeeName: req.employee ? req.employee.fullName : "Unbekannt",
                absenceReason: req.type,
                shiftDetails: shiftStr,
                comment: req.comment || req.reason || "",
                isPending: req.status === "open",
              })
            }
          })
        }

        this.loading = false
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Ferientage")
        this.holidayEvents = []
        this.loading = false
      }
    },
    showEvent({ nativeEvent, event }) {
      this.selectedEvent = event
      this.eventDialog = true
      nativeEvent.stopPropagation()
    },
    showDay({ date, nativeEvent }) {
      if (nativeEvent) nativeEvent.stopPropagation()
      this.selectedDay = date
      this.dayDialog = true
    },
    formatEventDates(event) {
      const start = this.$moment(event.start).format("DD.MM.YYYY")
      const end = this.$moment(event.end || event.start).format("DD.MM.YYYY")
      return start === end ? start : `${start} – ${end}`
    },
  },
  computed: {
    selectedDayEvents() {
      if (!this.selectedDay) return []
      const day = this.$moment(this.selectedDay)
      return this.holidayEvents
        .filter((event) => day.isBetween(event.start, event.end || event.start, "day", "[]"))
        .sort((a, b) => (a.employeeName || "").localeCompare(b.employeeName || "", "de-CH"))
    },
    selectedMonthCalendar() {
      return this.$moment(this.datePickerRange[0]).startOf("month").format("YYYY-MM-DD")
    },
  },
  watch: {
    async datePickerRange() {
      if (this.datePickerRange[0] && this.datePickerRange[1]) {
        this.fetchEntries()
      }
    },
    async selectedStudioSingle() {
      this.fetchEntries()
    },
  },
}
</script>

<style scoped></style>
